import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ContactInquiryStatus, contactInquiriesApi, inquiryNotesApi } from '@/services';

export const contactInquiriesKeys = {
  all: ['admin', 'contact-inquiries'] as const,
  list: () => [...contactInquiriesKeys.all, 'list'] as const,
  detail: (id: string) => [...contactInquiriesKeys.all, 'detail', id] as const,
};

export function useContactInquiriesListQuery() {
  return useQuery({
    queryKey: contactInquiriesKeys.list(),
    queryFn: () => contactInquiriesApi.getAll(),
  });
}

export function useContactInquiryDetailQuery(id: string) {
  return useQuery({
    queryKey: contactInquiriesKeys.detail(id),
    queryFn: () => contactInquiriesApi.getById(id),
    enabled: Boolean(id),
  });
}

export function useUpdateContactInquiryStatusMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: ContactInquiryStatus }) =>
      contactInquiriesApi.updateStatus(id, status),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: contactInquiriesKeys.list() });
      queryClient.invalidateQueries({ queryKey: contactInquiriesKeys.detail(id) });
    },
  });
}

export function useDeleteContactInquiryMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => contactInquiriesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactInquiriesKeys.all });
    },
  });
}

export function useAddContactInquiryNoteMutation(inquiryId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (content: string) =>
      inquiryNotesApi.create({ entityType: 'contact_inquiry', entityId: inquiryId, content }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactInquiriesKeys.detail(inquiryId) });
    },
  });
}

export function useDeleteContactInquiryNoteMutation(inquiryId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string) => inquiryNotesApi.delete(noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactInquiriesKeys.detail(inquiryId) });
    },
  });
}
